import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowDownToLine, ArrowUpFromLine, Gamepad2, History as HistoryIcon, ArrowLeft } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../context/LangContext";
import { formatCoins } from "../lib/format";

const ICONS = { deposit: ArrowDownToLine, withdraw: ArrowUpFromLine, game: Gamepad2 };
const FILTERS = ["all", "deposit", "withdraw", "game"];

export default function History() {
  const { t } = useLang();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/payments/activity").then(({ data }) => setItems(data.timeline || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const shown = filter === "all" ? items : items.filter((e) => e.type === filter);
  const stColor = (s) => s === "approved" || s === "win" ? "text-emerald-400" : s === "pending" ? "text-amber-400" : "text-rose-400";
  const label = { all: t("allGames"), deposit: t("deposit"), withdraw: t("withdrawal"), game: "Games" };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => nav(-1)} data-testid="history-back" className="h-9 w-9 grid place-items-center rounded-full border border-amber-500/30 text-amber-300"><ArrowLeft size={17} /></button>
        <h1 className="font-display text-3xl font-extrabold gold-text flex items-center gap-2"><HistoryIcon size={26} /> {t("txHistory")}</h1>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="history-filters">
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`history-filter-${f}`}
            className={`rounded-full px-4 py-1.5 text-xs font-bold border capitalize ${filter === f ? "btn-gold" : "border-amber-500/30 bg-black/30 text-amber-200"}`}>{label[f]}</button>
        ))}
      </div>

      <div className="mw-card divide-y divide-white/5" data-testid="history-list">
        {loading && <p className="p-6 text-center text-sm text-slate-500">…</p>}
        {!loading && shown.length === 0 && <p className="p-8 text-center text-sm text-slate-500">{t("noActivity")}</p>}
        {shown.map((e, i) => {
          const I = ICONS[e.type] || Gamepad2;
          return (
            <div key={i} className="px-4 py-3 flex items-center gap-3" data-testid={`history-item-${i}`}>
              <div className={`shrink-0 grid place-items-center h-9 w-9 rounded-lg ${e.type === "deposit" ? "bg-emerald-500/15 text-emerald-300" : e.type === "withdraw" ? "bg-cyan-500/15 text-cyan-300" : "bg-white/5 text-slate-300"}`}>
                <I size={16} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold capitalize truncate">{e.title} {e.meta && <span className="text-[11px] text-slate-500 font-normal">· {e.meta}</span>}</p>
                <p className="text-[10px] text-slate-500">{e.ts ? new Date(e.ts).toLocaleString() : ""} · <span className={stColor(e.status)}>{e.status}</span></p>
              </div>
              <span className={`font-mono-num font-bold text-sm ${e.type === "deposit" ? "text-emerald-400" : e.type === "withdraw" ? "text-cyan-400" : e.amount >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                {e.type === "deposit" ? "+" : e.type === "withdraw" ? "-" : e.amount >= 0 ? "+" : "-"}{formatCoins(Math.abs(e.amount))}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
